import Store from "../../../../Store";
import { chatResetMessage } from "../../../../Actions/GroupChatMessageActions";
import {
    formatUserIdToJid,
    getContactNameFromRoster,
    getDataFromRoster,
    getDataFromSDK,
    getLocalUserId
} from "../../../../Helpers/Chat/User";
import { getGroupData } from "../../../../Helpers/Chat/Group";
import { getArchivedChats } from "../../../../Helpers/Chat/ChatHelper";
import { jumpToMessage } from "../../../../Helpers/Chat/JumpToMessage";
import { CHAT_TYPE_GROUP, CHAT_TYPE_SINGLE } from "../../../../Helpers/Chat/Constant";
import { GLOBAL_SEARCH_TYPE } from "../../../../api/globalSearch";
import { decryptName, looksEncrypted } from "../../../../api/globalSearch/decrypt";

/** Strips the xmpp domain, so a jid works wherever a bare id is expected. */
export const toBareId = (value = "") => (typeof value === "string" ? value.replace(/@.*$/, "") : "");

/**
 * The group/contact id field name is not pinned down across every search type, so accept
 * the documented spelling plus the plausible alternatives rather than silently failing
 * to navigate when one is missing.
 */
export const getGroupId = (item = {}) => {
    const directId = toBareId(
        item.groupId || item.groupJid || item.jid || item.username || item.userId || item.id || ""
    );
    // A numeric row id is not a usable group id — fall through to the name lookup.
    if (directId && getGroupData(directId)) return directId;

    // Last resort: match the group by name against the groups already in the store, so a
    // renamed or missing id field still navigates.
    const name = item.groupName || item.name || "";
    if (name) {
        const { groupsData: { data: groups = [] } = {} } = Store.getState() || {};
        const match = groups.find((group) => group?.groupName === name);
        if (match?.groupId) return match.groupId;
    }
    return directId;
};

export const getContactId = (item = {}) =>
    toBareId(item.username || item.userId || item.userJid || item.jid || item.id || "");

/**
 * Maps the API's chat type onto the app's own constants ("chat" / "groupchat"), which
 * drive jid formatting, the avatar, and which conversation a click opens.
 *
 * The API has used more than one spelling for the same thing — "group" and "groupchat"
 * for groups, "singlechat" for 1:1 — so match on the substring rather than an exact
 * value. An exact check silently classified "group" as a single chat.
 */
export const normalizeChatType = (apiChatType, fallbackId = "") => {
    const value = String(apiChatType || "").toLowerCase();
    if (value.includes("group")) return CHAT_TYPE_GROUP;
    if (value) return CHAT_TYPE_SINGLE;
    // chatType missing from the payload — a known group id is the only reliable tell.
    return getGroupData(fallbackId) ? CHAT_TYPE_GROUP : CHAT_TYPE_SINGLE;
};

/** Ids whose profile has already been requested, so a re-render cannot re-trigger a fetch. */
const requestedProfiles = new Set();

/**
 * True when the roster holds a real display name for this user.
 *
 * getDataFromSDK can leave a stub roster entry behind ({ userId, userJid } with no
 * profile), and getContactNameFromRoster then falls back to echoing roster.userId.
 * That echo is a placeholder, not a name — treating it as resolved is what let the raw
 * id paint before the profile arrived.
 */
const hasResolvedName = (userId = "") => {
    if (!userId) return true;
    const roster = getDataFromRoster(userId);
    if (!roster || !Object.keys(roster).length) return false;
    const name = getContactNameFromRoster(roster);
    if (!name || typeof name !== "string") return false;
    if (looksEncrypted(name)) return false;
    // The id echoed back means no profile has landed yet.
    return name !== userId && name !== toBareId(userId);
};

/** Ids currently being watched for a name, so duplicate rows share one poll. */
const watchedProfiles = new Set();

/**
 * Polls until the roster can name this user, then notifies. Used for the tail case where
 * a profile lands after the pre-render prefetch has already given up waiting.
 */
const watchForName = (userId, onResolved, { timeoutMs = 15000, pollMs = 250 } = {}) => {
    if (watchedProfiles.has(userId)) return;
    watchedProfiles.add(userId);

    const deadline = Date.now() + timeoutMs;
    const timer = setInterval(() => {
        if (hasResolvedName(userId)) {
            clearInterval(timer);
            watchedProfiles.delete(userId);
            onResolved();
            return;
        }
        if (Date.now() >= deadline) {
            clearInterval(timer);
            watchedProfiles.delete(userId);
        }
    }, pollMs);
};

/**
 * Resolves a display name the way the rest of the app does: from the local roster or
 * group data. The API's own name fields are encrypted with an unknown key, so they are
 * only a last resort. When a user is missing from the roster their profile is fetched
 * in the background, which repopulates the store and re-renders with the real name.
 *
 * @param {function} [onResolved] called after a background profile fetch completes
 */
export const resolveDisplayName = (userId = "", chatType = CHAT_TYPE_SINGLE, fallback = "", onResolved) => {
    // Never surface an undecrypted value; the SDK hands back the original ciphertext
    // when profile decryption fails, and base64 is worse to read than the raw id.
    const safeFallback = looksEncrypted(fallback) ? "" : fallback;
    if (!userId) return safeFallback;

    if (chatType === CHAT_TYPE_GROUP) {
        const groupName = getGroupData(userId)?.groupName;
        if (groupName && !looksEncrypted(groupName)) return groupName;
        return safeFallback || userId;
    }

    if (hasResolvedName(userId)) {
        return getContactNameFromRoster(getDataFromRoster(userId));
    }

    if (!requestedProfiles.has(userId)) {
        requestedProfiles.add(userId);
        getDataFromSDK(userId).catch(() => requestedProfiles.delete(userId));
    }

    // A fetch is in flight — either just started here, or started by the prefetch and
    // not finished before its deadline. Watch for the name landing so the row swaps off
    // the raw id as soon as it is known.
    if (onResolved) watchForName(userId, onResolved);

    return safeFallback || userId;
};

/** The user ids whose names a page of results needs in order to render. */
const collectUserIds = (items = [], type) => {
    const ids = new Set();
    items.forEach((item) => {
        if (type === GLOBAL_SEARCH_TYPE.CONTACTS) {
            ids.add(getContactId(item));
            return;
        }
        if (type === GLOBAL_SEARCH_TYPE.GROUPS) return; // group names come from group data
        const { chatId, chatType } = resolveChatIdentity(item);
        if (chatType !== CHAT_TYPE_GROUP) ids.add(chatId);
    });
    ids.delete("");
    return [...ids];
};

/**
 * Fetches every missing profile for a page of results before it is rendered, so names
 * do not visibly change from a placeholder (the raw user id) to the real value after paint.
 *
 * Waits on the roster actually holding a name rather than on the fetch promise:
 * getDataFromSDK returns immediately without profile data when that user is already
 * being fetched elsewhere, so awaiting it alone does not mean the name is available.
 *
 * Always resolves — a profile that fails or is slow must not block the results, so the
 * wait is bounded and the row falls back to whatever it can show.
 */
export const prefetchDisplayNames = async (items = [], type, { timeoutMs = 8000, pollMs = 100 } = {}) => {
    const pending = collectUserIds(items, type).filter((id) => !hasResolvedName(id));
    if (!pending.length) return;

    pending.forEach((userId) => {
        if (requestedProfiles.has(userId)) return;
        requestedProfiles.add(userId);
        getDataFromSDK(userId).catch(() => requestedProfiles.delete(userId));
    });

    const deadline = Date.now() + timeoutMs;
    while (Date.now() < deadline) {
        if (pending.every(hasResolvedName)) return;
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => setTimeout(resolve, pollMs));
    }
};

/**
 * Works out which conversation a recent-chat / message hit belongs to.
 * For a group the conversation is the group; for a single chat it is the other party.
 */
export const resolveChatIdentity = (item = {}) => {
    const fromUser = toBareId(item.fromUser);
    const toUser = toBareId(item.toUser);
    const chatType = normalizeChatType(item.chatType, toUser);
    if (chatType === CHAT_TYPE_GROUP) {
        return { chatId: toUser, chatType };
    }
    const localUserId = toBareId(getLocalUserId());
    const chatId = fromUser === localUserId ? toUser : fromUser;
    return { chatId, chatType };
};

/** True when a result carries any signal that it is a group rather than an individual. */
const looksLikeGroup = (item = {}) =>
    Boolean(item.groupId) ||
    String(item.chatType || "").toLowerCase().includes("group") ||
    Boolean(getGroupData(getContactId(item)));

/**
 * Resolves the conversation a result opens, for any tab. The Contacts tab can carry both
 * individuals and groups, so a group-shaped result there is treated as a group (group chat,
 * group avatar) rather than being forced into a 1-to-1 chat.
 */
export const resolveResultTarget = (item = {}, type) => {
    if (type === GLOBAL_SEARCH_TYPE.GROUPS) {
        return { chatId: getGroupId(item), chatType: CHAT_TYPE_GROUP };
    }
    if (type === GLOBAL_SEARCH_TYPE.CONTACTS) {
        return looksLikeGroup(item)
            ? { chatId: getGroupId(item), chatType: CHAT_TYPE_GROUP }
            : { chatId: getContactId(item), chatType: CHAT_TYPE_SINGLE };
    }
    return resolveChatIdentity(item);
};

/**
 * True when a result should be hidden:
 *  - Archived chats are excluded from the Messages tab only. Recent Chats and Contacts
 *    still surface archived chats/contacts so they can be reopened.
 *  - A group the user is no longer in — absent from groupsData, it renders with the raw id
 *    for a name and fails to open ("no longer a participant"). Hidden on every tab.
 * Individual contacts are always shown.
 *
 * @param {Set<string>} [archivedIds] precomputed archived chat ids, to avoid rebuilding the
 *   set for every item in a page.
 */
export const shouldHideResult = (item = {}, type, archivedIds) => {
    const { chatId, chatType } = resolveResultTarget(item, type);

    if (type === GLOBAL_SEARCH_TYPE.MESSAGES) {
        if (item.archiveStatus === 1) return true;
        const archived = archivedIds || new Set(getArchivedChats());
        if (chatId && archived.has(chatId)) return true;
    }

    if (chatType !== CHAT_TYPE_GROUP) return false;
    return !getGroupData(chatId);
};

/** Drops results that don't belong on the tab (archived messages, groups the user left). */
export const filterAvailableResults = (items = [], type) => {
    // Only the Messages tab filters archived chats, so build the set only then.
    const archivedIds = type === GLOBAL_SEARCH_TYPE.MESSAGES ? new Set(getArchivedChats()) : undefined;
    return items.filter((item) => !shouldHideResult(item, type, archivedIds));
};

/**
 * Prefers the roster/group already in the store; falls back to the fields on the
 * search result so the row still renders a name and avatar for an unknown chat.
 */
export const resolveRoster = (item = {}, chatId = "", chatType = CHAT_TYPE_SINGLE) => {
    if (chatType === CHAT_TYPE_GROUP) {
        const groupData = getGroupData(chatId);
        if (groupData) return groupData;
        return {
            groupId: chatId,
            groupName: decryptName(item.groupName || item.toUserName, chatId),
            groupImage: item.image || item.toUserImage || "",
            thumbImage: item.thumbImage || item.toUserThumbImage || ""
        };
    }

    const rosterData = getDataFromRoster(chatId);
    if (rosterData && Object.keys(rosterData).length) return rosterData;

    // A contacts hit carries the profile directly rather than message fields.
    if (item.nickname) {
        const name = decryptName(item.nickname, chatId);
        return {
            userId: chatId,
            name,
            nickName: name,
            image: item.image,
            thumbImage: item.thumbImage
        };
    }

    // The peer sits on the "to" side only when the local user sent the message.
    const peerIsRecipient = toBareId(item.fromUser) !== chatId;
    const encryptedName = peerIsRecipient ? item.toUserName : item.fromUserName;
    const name = decryptName(encryptedName, chatId);
    return {
        userId: chatId,
        name,
        nickName: name,
        image: peerIsRecipient ? item.toUserImage : item.fromUserImage,
        thumbImage: peerIsRecipient ? item.toUserThumbImage : item.fromUserThumbImage
    };
};

/**
 * Builds the payload `handleConversationSectionDisplay` expects — it reads
 * `recent.chatType` / `recent.fromUserId` and derives chatId + jid from them.
 */
const buildActiveChatPayload = (item, chatId, chatType) => ({
    recent: {
        ...item,
        chatType,
        fromUserId: chatId,
        archiveStatus: item.archiveStatus || 0
    },
    roster: resolveRoster(item, chatId, chatType)
});

/**
 * Opens the conversation behind a Chats / Contacts / Groups search result.
 */
export const openChatFromSearchResult = (item = {}, type, handleConversationSectionDisplay) => {
    const { chatId, chatType } = resolveResultTarget(item, type);

    if (!chatId) {
        console.warn("[globalSearch] result has no usable chat id", item);
        return;
    }

    const payload = buildActiveChatPayload(item, chatId, chatType);
    Store.dispatch(chatResetMessage()).then(() => handleConversationSectionDisplay?.("block", payload));
};

/**
 * Opens the conversation a message belongs to and jumps to that message,
 * reusing the same path the starred messages list uses.
 */
export const openMessageFromSearchResult = (item = {}) => {
    const { chatId, chatType } = resolveChatIdentity(item);
    if (!chatId || !item.messageId) return;

    return jumpToMessage(
        {
            msgId: item.messageId,
            fromUserId: chatId,
            fromUserJid: formatUserIdToJid(chatId, chatType),
            chatType
        },
        "globalSearch"
    );
};
